import * as Sentry from '@sentry/browser'
import type { User } from 'firebase/auth'
import { z } from 'zod'
import { kFeatureFlagsRuntimePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getResponseDebugContext, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

export enum FlagSource {
  CHECKED_IN = 'checked-in',
  POSTHOG = 'posthog',
}

const RuntimeFlagsSchema = z.object({
  flags: z.record(z.boolean()),
  source: z.nativeEnum(FlagSource),
})
export type RuntimeFlagsResponse = z.infer<typeof RuntimeFlagsSchema>

/** Identity is authenticated by the API; the browser never supplies targeting properties. */
export async function getRuntimeFlags(user: User, signal: AbortSignal): Promise<RuntimeFlagsResponse> {
  const token = await user.getIdToken()
  signal.throwIfAborted()
  const requestUrl = `${kPaxtonAppApiRESTBaseUrl()}${kFeatureFlagsRuntimePath}`
  const response = await fetch(requestUrl, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
    signal,
  })
  if (!response.ok) throw new ApiError(response.status, 'Runtime feature flags unavailable')
  const { data, parseError } = await readResponseJson(response)
  // Fetch can resolve its headers before timeout or cleanup aborts the body read.
  signal.throwIfAborted()
  const parsed = RuntimeFlagsSchema.safeParse(data)
  if (parseError || !parsed.success) {
    const error = new ApiResponsePayloadError(response.status, 'Invalid runtime feature flag response')
    Sentry.captureException(error, {
      extra: {
        ...getResponseDebugContext(response, requestUrl),
        responseFailure: parseError ? 'body-read' : 'schema',
        // Parser messages and schema inputs can contain response data; record only structure.
        parseErrorName: parseError instanceof Error ? parseError.name : null,
        validationIssues: !parseError && !parsed.success ? parsed.error.issues.map(({ code, path }) => ({ code, path })) : null,
      },
    })
    throw error
  }
  return parsed.data
}
